import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { Skeleton } from "../../../../atoms/skeleton";
import { PAGE_NAME } from "../../../../../constants/constants";
import { useBoxCollaboratorDetailsQuery } from "../../../../../store/box/boxSlice";
import { useLocation, useParams } from "react-router-dom";
import { Input } from "../../../../atoms/input";
import { Table } from "../../../../atoms/table";
import { Divider } from "../../../../atoms/divider";
import { Dropdown } from "../../../../atoms/dropdown";
import { Pagination } from "../../../../molecules/pagination";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { isEmail, pascalToSnakeCase } from "../../../../../utils";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { useCollaborationHooks } from "./useCollaborationHooks";

const BoxCollaboratorDetails = () => {
    const pageClassName = "p-boxCollaboratorDetails";
    const [email, setEmail] = React.useState<string>('');
    const { id } = useParams();
    const { state } = useLocation();

    React.useEffect(() => {
        if (isEmail(id as string)) {
            setEmail(id as string);
        } else if (state && state.row.email && isEmail(state.row.email)) {
            setEmail(state.row.email as string);
        }
    }, [email, id, state]);

    const {
        search,
        query,
        pageChangeHandler,
        sortHandler,
        limitChangeHandler,
        searchHandler,
    } = useTableHooks();

    const { data, isLoading, isFetching } = useBoxCollaboratorDetailsQuery({
        email,
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(query.sortBy),
        sort_direction: query.sortDirection,
    });

    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: true,
        },
        {
            title: "Name",
            dataIndex: "name",
            isSortable: true,
        },
        {
            title: "Email",
            dataIndex: "email",
            isSortable: true,
        },
        {
            title: "Action",
            dataIndex: "action",
            isSortable: false,
        },
        {
            title: "IP Address",
            dataIndex: "ipAddress",
            isSortable: true,
        },
        {
            title: "Parent Folder",
            dataIndex: "parentFolder",
            isSortable: true,
        },
        {
            title: "Last Activity",
            dataIndex: "activityDate",
            isSortable: true,
        },
    ];

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    const {
        collaborationSearch,
        collaborationQuery,
        collaborationSortHandler,
        collaborationLimitChangeHandler,
        collaborationSearchHandler,
        collaboration,
        collaborationLoading,
        collaborationFetching,
        collaborationTotalData,
        collaborationHandleFirstPage,
        collaborationHandlePrevPage,
        collaborationHandleLastPage,
        collaborationHandleNextPage,
        collaborationRenderPageNumbers,
        collaborationTableHeader,
    } = useCollaborationHooks({
        email: email as string,
    });

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>
                {PAGE_NAME.BOX_COLLABORATOR_DETAILS}
            </p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__container__item`}>
                    <p className={`${pageClassName}__container__item__title`}>Name</p>
                    <p className={`${pageClassName}__container__item__value`}>
                        {isLoading ? (
                            <Skeleton height="small" />
                        ) : data?.result?.name ? (
                            data?.result?.name
                        ) : collaboration?.result?.data[0]?.name ? (
                            collaboration?.result?.data[0]?.name
                        ) : collaboration?.result?.data[0]?.oktaUser ? (
                            collaboration?.result?.data[0]?.oktaUser?.firstName +
                            " " +
                            collaboration?.result?.data[0]?.oktaUser?.lastName
                        ) : (
                            "-"
                        )}
                    </p>
                </div>
                <div className={`${pageClassName}__container__item`}>
                    <p className={`${pageClassName}__container__item__title`}>Email</p>
                    <p className={`${pageClassName}__container__item__value`}>
                        {isLoading ? (
                            <Skeleton height="small" />
                        ) : (
                            data?.result?.email ?? "-"
                        )}
                    </p>
                </div>
                {collaboration?.result?.data[0] && (
                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Status</p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {collaborationLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                collaboration?.result?.data[0].status ?? "-"
                            )}
                        </p>
                    </div>
                )}

                {/* ********************* */}
                <div className={`${pageClassName}__container__table`}>
                    <p className={`${pageClassName}__subtitle`}>Collaboration Details</p>

                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={(e) => {
                                collaborationSearchHandler(e);
                                collaborationHandleFirstPage();
                            }}
                            value={collaborationSearch}
                        />
                    </div>
                    <div className={`${pageClassName}__table`}>
                        <Table
                            header={collaborationTableHeader}
                            data={collaboration?.result?.data ?? []}
                            loading={collaborationFetching}
                            firstLoad={collaborationLoading}
                            sortHandler={collaborationSortHandler}
                            minWidth="2400"
                            minHeight="330"
                            startIndex={collaboration?.result?.from}
                            sortBy={collaborationQuery.sortBy}
                        />
                    </div>
                    <Divider />

                    <div className={`${pageClassName}__pagination`}>
                        <div className={`${pageClassName}__pagination__limit`}>
                            <p>Display</p>
                            <div className={`${pageClassName}__pagination__dropdown`}>
                                <Dropdown
                                    options={[
                                        { label: "20", value: 20 },
                                        { label: "50", value: 50 },
                                        { label: "100", value: 100 },
                                    ]}
                                    selected={{ label: query.limit, value: query.limit }}
                                    onClickChange={(e) => {
                                        collaborationLimitChangeHandler(e.value as number);
                                        collaborationHandleFirstPage();
                                    }}
                                    isBordered
                                    isIcon
                                />
                            </div>
                        </div>
                        <div className={`${pageClassName}__pagination__number`}>
                            {collaboration?.result?.total ? (
                                <p>
                                    Show {collaboration?.result?.from} -{" "}
                                    {collaboration?.result?.from + query.limit - 1 <
                                        collaboration?.result?.total
                                        ? collaboration?.result?.from + query.limit - 1
                                        : collaboration?.result?.total}{" "}
                                    of {collaboration?.result?.total}
                                </p>
                            ) : (
                                <p>Show 00 - 00 of 00</p>
                            )}
                        </div>
                        <div className={`${pageClassName}__pagination__nav`}>
                            {collaboration?.result?.total > query.limit && (
                                <Pagination
                                    totalData={collaborationTotalData}
                                    page={collaborationQuery.current}
                                    handleFirstPage={collaborationHandleFirstPage}
                                    handlePrevPage={collaborationHandlePrevPage}
                                    handleLastPage={collaborationHandleLastPage}
                                    handleNextPage={collaborationHandleNextPage}
                                    renderPageNumbers={collaborationRenderPageNumbers}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {/* ********************* */}
                <div className={`${pageClassName}__container__table`}>
                    <p className={`${pageClassName}__subtitle`}>Collaborator Logs</p>

                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={(e) => {
                                searchHandler(e);
                                handleFirstPage();
                            }}
                            value={search}
                        />
                    </div>
                    <div className={`${pageClassName}__table`}>
                        <Table
                            header={tableHeader}
                            data={data?.result?.logs ?? []}
                            loading={isFetching}
                            firstLoad={isLoading}
                            sortHandler={sortHandler}
                            minWidth="1440"
                            minHeight="330"
                            startIndex={data?.result?.from}
                            sortBy={query.sortBy}
                        />
                    </div>
                    <Divider />

                    <div className={`${pageClassName}__pagination`}>
                        <div className={`${pageClassName}__pagination__limit`}>
                            <p>Display</p>
                            <div className={`${pageClassName}__pagination__dropdown`}>
                                <Dropdown
                                    options={[
                                        { label: "20", value: 20 },
                                        { label: "50", value: 50 },
                                        { label: "100", value: 100 },
                                    ]}
                                    selected={{ label: query.limit, value: query.limit }}
                                    onClickChange={(e) => {
                                        limitChangeHandler(e.value as number);
                                        handleFirstPage();
                                    }}
                                    isBordered
                                    isIcon
                                />
                            </div>
                        </div>
                        <div className={`${pageClassName}__pagination__number`}>
                            {data?.result?.total ? (
                                <p>
                                    Show {data?.result?.from} -{" "}
                                    {data?.result?.from + query.limit - 1 < data?.result?.total
                                        ? data?.result?.from + query.limit - 1
                                        : data?.result?.total}{" "}
                                    of {data?.result?.total}
                                </p>
                            ) : (
                                <p>Show 00 - 00 of 00</p>
                            )}
                        </div>
                        <div className={`${pageClassName}__pagination__nav`}>
                            {data?.result?.total > query.limit && (
                                <Pagination
                                    totalData={totalData}
                                    page={query.current}
                                    handleFirstPage={handleFirstPage}
                                    handlePrevPage={handlePrevPage}
                                    handleLastPage={handleLastPage}
                                    handleNextPage={handleNextPage}
                                    renderPageNumbers={renderPageNumbers}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BoxCollaboratorDetails;
